/**
 * The wire, with the two things that make a game feel bad: the time a message
 * spends in flight, and the times it spends longer than that.
 *
 * Nothing here reads a clock — the caller says what time it is — so a test
 * plays a whole laggy match in a millisecond and gets the same answer twice.
 */

/** A message never overtakes the one in front: this stands for a WebSocket. */
export interface LinkOptions {
  /** One-way flight time in ms. The ping the HUD shows is two of these. */
  latency?: number;
  /** How much longer a message may take. Never less: a queue only ever adds. */
  jitter?: number;
  seed?: number;
}

/**
 * A message is never lost here, because a WebSocket is TCP and does not lose
 * them. A dropped packet costs a **stall** while the network sends it again,
 * and then everything it was holding lands at once — which is the case the
 * smoothing has to survive, and the one `stall` reproduces.
 */
export function createLink<T>({
  latency = 0,
  jitter = 0,
  seed = 1,
}: LinkOptions = {}) {
  const queue: { message: T; arrival: number }[] = [];
  const wander = seededRandom(seed);
  let lastArrival = -Infinity;
  let openAt = 0;

  return { send, receive, stall, waiting: () => queue.length };

  function send(message: T, now: number) {
    const arrival = Math.max(lastArrival, now + latency + wander() * jitter);

    lastArrival = arrival;
    queue.push({ message, arrival });
  }

  function receive(now: number): T[] {
    if (now < openAt) return [];

    const ready: T[] = [];

    while (queue.length > 0 && queue[0].arrival <= now) {
      ready.push(queue.shift()!.message);
    }

    return ready;
  }

  /** The hiccup: nothing lands for a while, and then all of it lands together. */
  function stall(duration: number, now: number) {
    openAt = now + duration;
  }
}

/** A wander that repeats itself: a measurement that flakes measures nothing. */
function seededRandom(seed: number) {
  let state = seed % 2147483647 || 1;

  return () => {
    state = (state * 16807) % 2147483647;
    return state / 2147483647;
  };
}
